// Framework-free WebSocket connection manager for /ws/analysis.
// Ported from mobile src/hooks/useAnalysisWebSocket.ts, with React Native
// specifics (AppState, expo secure-store token) replaced by the browser
// `visibilitychange` event and an env-configured token.
//
// A module-level singleton: useAnalysisWebSocket() calls start() once on mount.
// Subscribers (useAnalysisSubscription) re-send their own `subscribe` when
// `sendMessage` transitions to non-null, so this client never iterates the
// active key set on open.
import { buildAnalysisWsUrl } from 'config';
import { freshWsToken } from 'ws/wsAuth';
import { useAnalysisWsStore } from 'store/useAnalysisWsStore';
import type { AnalysisWsInbound, AnalysisWsOutbound } from 'types/analysisWs';

const MAX_RECONNECT_ATTEMPTS = 5;

class AnalysisWsClient {
  private ws: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private started = false;
  private isVisible = true;
  // Bumped on every connect() so an in-flight async token fetch can detect it has
  // been superseded by a newer connect()/stop() and abort before opening a socket.
  private connectGen = 0;

  private get store() {
    return useAnalysisWsStore.getState();
  }

  /** Idempotent — safe to call from a mounting effect in React StrictMode. */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.isVisible = document.visibilityState !== 'hidden';
    document.addEventListener('visibilitychange', this.handleVisibility);
    this.connect();
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    document.removeEventListener('visibilitychange', this.handleVisibility);
    this.clearReconnectTimer();
    this.closeSocket();
    this.store.setSendMessage(null);
  }

  /** Force an immediate reconnect (e.g. from a manual "reconnect" affordance). */
  reconnect(): void {
    this.reconnectAttempts = 0;
    this.connect();
  }

  private handleVisibility = (): void => {
    const visible = document.visibilityState !== 'hidden';
    this.isVisible = visible;
    if (!visible) {
      this.clearReconnectTimer();
      this.closeSocket();
      this.store.setSendMessage(null);
    } else if (this.started) {
      this.reconnectAttempts = 0;
      this.connect();
    }
  };

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private closeSocket(): void {
    const ws = this.ws;
    if (ws) {
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      try {
        ws.close();
      } catch {
        // ignore errors closing a partially-opened socket
      }
      this.ws = null;
    }
  }

  private connect(): void {
    this.closeSocket();
    this.clearReconnectTimer();
    if (!this.started) return;

    this.store.setConnectionStatus('connecting');
    // Resolving (and possibly refreshing) the auth token is async, so hand off to
    // openSocket. connectGen guards against a stale continuation opening a socket.
    void this.openSocket(++this.connectGen);
  }

  private async openSocket(gen: number): Promise<void> {
    let token: string;
    try {
      token = await freshWsToken();
    } catch {
      // A failed token refresh shouldn't wedge the socket — try anonymously and
      // let the server (and the backoff loop) decide.
      token = '';
    }

    // A newer connect(), a stop(), or a tab-hidden event ran during the await —
    // abandon this attempt so we never open (or leak) a superseded socket.
    if (gen !== this.connectGen || !this.started || !this.isVisible) return;

    const ws = new WebSocket(buildAnalysisWsUrl(token));
    this.ws = ws;

    ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.store.setConnectionStatus('connected');

      // Register the send function so consumer hooks can dispatch
      // subscribe/unsubscribe without a direct reference to the socket.
      const sendFn = (msg: AnalysisWsOutbound) => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify(msg));
        }
      };
      this.store.setSendMessage(sendFn);

      // Flush unsubscribes buffered while the socket was down so the server
      // can't be left holding orphan subscriptions. Subscribers re-send their
      // own `subscribe` when they observe sendMessage flip to non-null.
      this.store.drainPendingUnsubscribes().forEach(sendFn);
    };

    ws.onmessage = (event: MessageEvent<string>) => {
      try {
        const msg = JSON.parse(event.data) as Partial<AnalysisWsInbound>;
        if (
          typeof msg.symbol === 'string' &&
          typeof msg.timeframe === 'string' &&
          typeof msg.timestamp === 'number' &&
          msg.data !== undefined
        ) {
          // Disambiguate panels sharing symbol+timeframe: market comes from the
          // frame, exchange from the payload (e.g. "Binance" → "binance").
          const market = typeof msg.market === 'string' ? msg.market : undefined;
          const exchange =
            typeof msg.data.exchange === 'string' ? msg.data.exchange.toLowerCase() : undefined;
          this.store.receiveAnalysis(msg.symbol, msg.timeframe, msg.timestamp, msg.data, market, exchange);
        }
      } catch {
        // skip malformed frames
      }
    };

    ws.onerror = () => {
      // onclose always fires after onerror — reconnect logic lives there
    };

    ws.onclose = () => {
      this.store.setSendMessage(null);
      if (!this.started || !this.isVisible) return;

      this.ws = null;

      const attempts = this.reconnectAttempts;
      if (attempts >= MAX_RECONNECT_ATTEMPTS) {
        this.store.setConnectionStatus('error');
        return;
      }

      this.store.setConnectionStatus('disconnected');
      this.reconnectAttempts += 1;
      const delay = Math.min(1_000 * 2 ** attempts, 30_000);

      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        if (this.started) this.connect();
      }, delay);
    };
  }
}

export const analysisWsClient = new AnalysisWsClient();
